import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useExamStore } from "@/stores/examStore";
import { useSubmitAnswer, useCompleteExam } from "@/hooks/useExam";
import ExamTimer from "@/components/exam/ExamTimer";
import QuestionCard from "@/components/exam/QuestionCard";
import ExamProgress from "@/components/exam/ExamProgress";

export default function TakeExam() {
  const { id: _id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSubmitError, setShowSubmitError] = useState(false);

  const {
    currentExam,
    currentAttemptId,
    currentQuestionIndex,
    answers,
    timeRemaining,
    isSubmitting,
    answerQuestion,
    nextQuestion,
    prevQuestion,
    goToQuestion,
    setTimeRemaining,
    setSubmitting,
    reset,
  } = useExamStore();

  const submitAnswerMutation = useSubmitAnswer(currentAttemptId || "");
  const completeExamMutation = useCompleteExam();

  const handleSelectAnswer = (answerId: string) => {
    if (!currentExam) return;
    const question = currentExam.questions[currentQuestionIndex];
    if (!question) return;

    answerQuestion(question.id, answerId);

    if (currentAttemptId) {
      submitAnswerMutation.mutate({
        question_id: question.id,
        selected_answer_id: answerId,
      });
    }
  };

  const handleCompleteExam = useCallback(async () => {
    if (!currentAttemptId) return;
    setSubmitting(true);
    try {
      await completeExamMutation.mutateAsync(currentAttemptId);
      const attemptId = currentAttemptId;
      reset();
      navigate(`/attempts/${attemptId}/results`);
    } catch {
      setSubmitting(false);
      setShowConfirm(false);
      setShowSubmitError(true);
    }
  }, [currentAttemptId, completeExamMutation, setSubmitting, reset, navigate]);

  const handleTimeUp = useCallback(() => {
    handleCompleteExam();
  }, [handleCompleteExam]);

  const handleTick = useCallback(
    (seconds: number) => {
      setTimeRemaining(seconds);
    },
    [setTimeRemaining],
  );

  if (!currentExam || !currentAttemptId) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h2 className="text-xl font-bold text-gray-900">No exam in progress</h2>
        <p className="mt-2 text-gray-500">
          Your session may have expired. If you were taking the free trial, please check your history to see if it was submitted.
        </p>
        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            onClick={() => navigate("/exams")}
            className="rounded-lg bg-primary-600 px-6 py-2 text-sm font-semibold text-white hover:bg-primary-700 transition-colors"
          >
            Browse Exams
          </button>
          <button
            onClick={() => navigate("/history")}
            className="rounded-lg border border-gray-300 px-6 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            View History
          </button>
        </div>
      </div>
    );
  }

  const question = currentExam.questions[currentQuestionIndex];
  if (!question) return null;

  const selectedAnswerId = answers.get(question.id);
  const totalQuestions = currentExam.questions.length;

  const answeredSet = new Set<number>();
  currentExam.questions.forEach((q, i) => {
    if (answers.has(q.id)) {
      answeredSet.add(i);
    }
  });

  return (
    <div className="page-enter mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6">
      {showSubmitError && (
        <div className="mb-4 rounded-lg bg-danger-50 border border-danger-200 px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-danger-700">Failed to submit exam. Please try again.</p>
          <button onClick={() => setShowSubmitError(false)} className="text-danger-500 hover:text-danger-700 ml-4 text-lg leading-none">&times;</button>
        </div>
      )}

      {/* Top bar with timer and submit */}
      <div className="flex items-center justify-between mb-6 sticky top-0 bg-gray-50 py-3 z-10 border-b border-gray-200">
        <div className="flex items-center space-x-4">
          <h1 className="text-lg font-bold text-gray-900 hidden sm:block">{currentExam.title}</h1>
          <ExamTimer
            timeRemaining={timeRemaining}
            onTick={handleTick}
            onTimeUp={handleTimeUp}
          />
        </div>
        <button
          onClick={() => setShowConfirm(true)}
          disabled={isSubmitting}
          className="rounded-lg bg-success-600 px-5 py-2 text-sm font-semibold text-white hover:bg-success-700 disabled:opacity-50 transition-colors"
        >
          Submit Exam
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Question area */}
        <div className="lg:col-span-2">
          <QuestionCard
            question={question}
            questionNumber={currentQuestionIndex + 1}
            totalQuestions={totalQuestions}
            selectedAnswerId={selectedAnswerId}
            onSelectAnswer={handleSelectAnswer}
          />

          {/* Navigation buttons */}
          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={prevQuestion}
              disabled={currentQuestionIndex === 0}
              className="flex items-center space-x-2 rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Previous</span>
            </button>

            <span className="text-sm text-gray-500">
              {currentQuestionIndex + 1} / {totalQuestions}
            </span>

            <button
              onClick={nextQuestion}
              disabled={currentQuestionIndex === totalQuestions - 1}
              className="flex items-center space-x-2 rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <span>Next</span>
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Side panel with progress */}
        <div className="lg:col-span-1">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm sticky top-20">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Question Navigator</h3>
            <ExamProgress
              totalQuestions={totalQuestions}
              currentIndex={currentQuestionIndex}
              answeredQuestions={answeredSet}
              onNavigate={goToQuestion}
            />
          </div>
        </div>
      </div>

      {/* Confirm dialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900">Submit Exam?</h3>
            <p className="mt-2 text-sm text-gray-500">
              You have answered {answers.size} out of {totalQuestions} questions.
              {answers.size < totalQuestions && (
                <span className="block mt-1 text-warning-600 font-medium">
                  You have {totalQuestions - answers.size} unanswered questions.
                </span>
              )}
            </p>
            <p className="mt-2 text-sm text-gray-500">
              Once submitted, you cannot change your answers.
            </p>
            <div className="mt-6 flex space-x-3 justify-end">
              <button
                onClick={() => setShowConfirm(false)}
                className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Continue Exam
              </button>
              <button
                onClick={() => {
                  setShowConfirm(false);
                  handleCompleteExam();
                }}
                disabled={isSubmitting}
                className="rounded-lg bg-success-600 px-5 py-2 text-sm font-semibold text-white hover:bg-success-700 disabled:opacity-50 transition-colors"
              >
                {isSubmitting ? "Submitting..." : "Submit Exam"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
